import React, { useCallback } from 'react';
import { Sliders, User, Activity, Target, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета калорий
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  activityLevels,
  weightGoals
}) => {
  
  // Расчет текущего веса
  const weight = parseFloat(formData.weight) || 0;
  
  // Расчет текущего роста
  const height = parseFloat(formData.height) || 0;
  
  // Расчет ИМТ (индекса массы тела)
  const calculateBMI = () => {
    if (!weight || !height) return 0;
    const heightInMeters = height / 100;
    return weight / (heightInMeters * heightInMeters);
  };
  
  const bmi = calculateBMI();
  
  // Определение категории ИМТ
  const getBMICategory = () => {
    if (bmi < 16) return { label: 'Выраженный дефицит', color: 'text-red-600 dark:text-red-400' };
    if (bmi < 18.5) return { label: 'Недостаточный вес', color: 'text-amber-600 dark:text-amber-400' };
    if (bmi < 25) return { label: 'Норма', color: 'text-emerald-600 dark:text-emerald-400' };
    if (bmi < 30) return { label: 'Избыточный вес', color: 'text-amber-600 dark:text-amber-400' };
    if (bmi < 35) return { label: 'Ожирение I', color: 'text-red-600 dark:text-red-400' };
    if (bmi < 40) return { label: 'Ожирение II', color: 'text-red-700 dark:text-red-500' };
    return { label: 'Ожирение III', color: 'text-red-800 dark:text-red-600' };
  };
  
  const bmiCategory = getBMICategory();
  
  // Обработчик изменения веса
  const handleWeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      weight: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.weight) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.weight;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения возраста
  const handleAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.age) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.age;
        return newErrors;
      });
    }
  };
  
  // Функция для установки ошибок - создаем ее здесь, так как нам не передали setErrors напрямую
  const setErrors = (updater) => {
    // В данном компоненте мы просто игнорируем ошибки, так как
    // эта функция здесь используется только для очистки ошибок
    // при изменении значений слайдеров
  };
  
  // Функция расчета
  const calculate = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные веса для быстрого выбора
  const predefinedWeights = formData.gender === 'male'
    ? [{ value: '65', label: '65 кг' }, { value: '75', label: '75 кг' }, { value: '85', label: '85 кг' }, { value: '95', label: '95 кг' }]
    : [{ value: '50', label: '50 кг' }, { value: '60', label: '60 кг' }, { value: '70', label: '70 кг' }, { value: '80', label: '80 кг' }];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sliders className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Индикатор ИМТ */}
        <div className="mb-5 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-1 text-sm">
            <span className="text-gray-600 dark:text-gray-400">Индекс массы тела (ИМТ):</span>
            <span className={`font-medium ${bmiCategory.color}`}>
              {formatNumber(bmi, 1)} - {bmiCategory.label}
            </span>
          </div>
          
          {/* Индикатор ИМТ */}
          <div className="relative w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full mt-2 mb-1">
            {/* Отметки ИМТ */}
            <div className="absolute top-0 left-0 h-full w-full">
              <div className="absolute left-0 w-[18.5%] h-full bg-red-500 rounded-l-full"></div>
              <div className="absolute left-[18.5%] w-[6.5%] h-full bg-amber-500"></div>
              <div className="absolute left-[25%] w-[25%] h-full bg-emerald-500"></div>
              <div className="absolute left-[50%] w-[50%] h-full bg-gradient-to-r from-amber-500 via-red-500 to-red-800 rounded-r-full"></div>
            </div>
            
            {/* Маркер текущего ИМТ */}
            <div 
              className="absolute top-0 w-4 h-4 bg-white dark:bg-gray-200 rounded-full shadow border-2 border-emerald-500 -mt-1.5"
              style={{ 
                left: `${Math.max(0, Math.min(100, (bmi / 40) * 100))}%`,
                transform: 'translateX(-50%)'
              }}
            ></div>
          </div>
          
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
            <span>16</span>
            <span>18.5</span>
            <span>25</span>
            <span>30</span>
            <span>40</span>
          </div>
        </div>
        
        {/* Вес */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="weightSlider" className="text-gray-600 dark:text-gray-400">Вес</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.weight} кг</span>
          </div>
          <input
            type="range"
            id="weightSlider"
            min="40"
            max="150"
            step="0.5"
            value={formData.weight}
            onChange={handleWeightChange}
            aria-label="Вес"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными весами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedWeights.map(weight => (
              <button
                key={weight.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, weight: weight.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.weight === weight.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {weight.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Возраст */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="ageSlider" className="text-gray-600 dark:text-gray-400">Возраст</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.age} лет</span>
          </div>
          <input
            type="range"
            id="ageSlider"
            min="18"
            max="80"
            step="1"
            value={formData.age}
            onChange={handleAgeChange}
            aria-label="Возраст"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными возрастами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['25', '35', '45', '55'].map(age => (
              <button
                key={age}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, age }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.age === age 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {age} лет
              </button>
            ))}
          </div>
        </div>
        
        {/* Уровень активности */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Уровень активности</label>
          </div>
          <div className="grid grid-cols-1 gap-1">
            {activityLevels.map(level => (
              <button
                key={level.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, activityLevel: level.id }))}
                className={`p-2 text-left text-sm rounded-lg border transition-colors
                  ${formData.activityLevel === level.id 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <span className="font-medium">{level.name}</span>
                <span className="text-xs block mt-0.5 opacity-80">{level.description}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Цель */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Цель</label>
          </div>
          <div className="grid grid-cols-1 gap-1">
            {weightGoals.slice(0, 4).map(goal => (
              <button
                key={goal.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, goal: goal.id }))}
                className={`p-2 text-left text-sm rounded-lg border transition-colors
                  ${formData.goal === goal.id 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <span className="font-medium">{goal.name}</span>
                <span className="text-xs block mt-0.5 opacity-80">{goal.description}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Пол */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Пол</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'male' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center">
                <User className="w-4 h-4 mr-1" />
                Мужской
              </div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center">
                <User className="w-4 h-4 mr-1" />
                Женский
              </div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculate}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать калории
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;